import { pathOr } from 'ramda';
import styled, { css } from 'styled-components';
import type { EnvironmentFlags } from '@peloton/env';
import { withEnvFlags } from '@peloton/env';
import { toCloudinarySrc } from '@peloton/images';
import { media } from '@peloton/styles';
import { Image } from '@ecomm/image';
import PerformantImage from '@ecomm/image/PerformantImage';
import type { LinkProps } from '@ecomm/links';
import { Link } from '@ecomm/links';
import { hr3, secondaryHeading } from '@ecomm/typography';
import type { ContentContainerProps } from './models';
import { activeTheme } from './themes';
import type { Alignment, BreakpointType } from './types';

// Re-export Alignment for backwards compatibility
export type { Alignment } from './types';

export const Container = styled.div`
  padding: 0 20px;
  margin: 20px 0;
  display: flex;
  flex-direction: column;

  ${media.desktopLarge`
    padding: 0;
    margin: 0;
    flex-direction: ${(props: { contentAlignment: Alignment }) =>
      props.contentAlignment === 'left' ? `row-reverse` : `row`};;
  `}
`;

const ImageWrapperStyles = css`
  overflow: hidden;
  ${media.desktopLarge`
    flex: 3.69;
  `}
  ${media.infinity`
    flex: 2.5
  `}
`;

export const StyledImageLink = (styled(Link)`
  ${ImageWrapperStyles};
  background-color: ${props => activeTheme(props.theme, 'contentBackground')};
  &:focus {
    outline: 0;
  }
  &:hover {
    img {
      transform: scale(1.03);
      opacity: 0.8;
    }
  }
` as any) as React.ComponentType<React.PropsWithChildren<LinkProps>>;

const blockImageStyles = css`
  min-height: 100%;
  object-fit: cover;
  transition: all 500ms ease-in-out;
  vertical-align: middle;
`;

export const BlockImage = styled(Image)`
  ${blockImageStyles}
  max-width: 100%;
`;
export const BlockPerformantImage = styled(PerformantImage)`
  ${blockImageStyles}

  img {
    max-width: 100%;
  }
`;

const backgroundImageWidths = {
  mobile: 600,
  tablet: 800,
  desktop: 1000,
  desktopLarge: 1600,
};

const contentContainerHeights = {
  mobile: 425,
  tablet: 655,
  desktop: 655,
};

const getContainerHeight = (breakpoint: BreakpointType) => {
  // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type 'BreakpointType' can't be used to index type '{ mobile: number; tablet: number; desktop: number; }'.
  const height = contentContainerHeights[breakpoint];
  return height ? `${height}px` : `inherit`;
};

const styledBackground = (
  { blockBackgroundData, theme }: ContentContainerProps,
  breakpoint: BreakpointType,
) => {
  const url: string = pathOr('', [breakpoint, 'url'], blockBackgroundData);
  const cloudinarizedUrl = toCloudinarySrc({
    src: url,
    width: backgroundImageWidths[breakpoint],
  });
  const containerHeight = getContainerHeight(breakpoint);

  return blockBackgroundData
    ? `
      background: url(${cloudinarizedUrl}) no-repeat bottom center;
      background-size: cover;
      height: ${containerHeight};
      `
    : `background-color: ${activeTheme(theme, 'contentBackground')};`;
};

export const ContentContainer = withEnvFlags(styled.div<
  ContentContainerProps & EnvironmentFlags
>`
  ${(props: ContentContainerProps & EnvironmentFlags) =>
    styledBackground(props, 'mobile')}

  ${media.tablet`
    ${(props: ContentContainerProps & EnvironmentFlags) =>
      styledBackground(props, 'tablet')}
  `}
  ${media.desktop`
    ${(props: ContentContainerProps & EnvironmentFlags) =>
      styledBackground(props, 'desktop')}
  `}
  ${media.desktopLarge`
    flex: 2;
    ${(props: ContentContainerProps & EnvironmentFlags) =>
      styledBackground(props, 'desktopLarge')}
  `}
  ${media.desktopXLarge`
    display: flex;
    justify-content: center;
  `};
`) as React.ComponentType<React.PropsWithChildren<ContentContainerProps>>;

export const smallPrint = css`
  ${secondaryHeading}
  font-size: 13px;

  ${media.tablet`
    font-size: 14px;
  `}
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 30px 20px 30px;
  color: ${props => activeTheme(props.theme, 'bodyColor')};

  ${media.tablet`
    padding: 60px;
    align-items: center;
    text-align: center;
  `}

  ${media.desktopLarge`
    padding: ${props => activeTheme(props.theme, 'desktopLargeContentPadding')};
    align-items: flex-start;
    text-align: left;
    justify-content: ${props => activeTheme(props.theme, 'desktopLargeContentAlignment')};
  `}

  ${media.desktopXLarge`
    padding: ${props => activeTheme(props.theme, 'desktopXLargeContentPadding')};
    & > * { max-width: ${props => activeTheme(props.theme, 'maxContentWidth')};}
  `}

  .product-block-title {
    ${hr3}
    color: ${props => activeTheme(props.theme, 'headingColor')};
    margin-bottom: 10px;

    ${media.desktopLarge`
      margin-bottom: 35px;
    `}
  }

  .product-block-body {
    ${secondaryHeading}
    font-size: 14px;

    ${media.tablet`
      font-size: 18px;
    `}
  }

  .product-block-fineprint {
    ${smallPrint}
    margin-top: 3px;
    color: ${props => activeTheme(props.theme, 'finePrintColor')};
  }
` as React.ComponentType<React.PropsWithChildren<ContentContainerProps>>;
